import { PROGRESS_FOSSID_STATUS } from "@constants/index";
import { ScanToolSearchInfoContext } from "@components/Guards/ScanToolGuard";
import { useTheme } from "@hooks/useTheme";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Communication from "../../../components/Communication/Communication";
import {
  AnalysisIcon,
  CheckAnalysisIcon,
  CheckFollowUpIcon,
  FollowUpIcon,
  LoadIdentifyIcon,
  ReadyIcon,
  ReviewIcon,
  UsageTypeCheckIcon,
  UsageTypeInputIcon,
} from "../../../components/Icons";
import { ComponentTable, ComponentTableHeader } from "../../../components/Tables";
import {
  BasicInfoSection,
  Breadcrumb,
  FulfillmentInfo,
  VerificationReportInfo,
  WorkflowProgressFossID,
} from "../../../components/UI";
import queryKeys from "../../../configs/react-query/query-key";
import { useClarityAirVerification } from "../../../hooks/useClarityAirVerification";
import { useMutationData } from "../../../hooks/useMutationData";
import useProjectMembers from "../../../hooks/useProjectMembers";
import { formatFullDateTime } from "@utils/common";

const ClarityAIRVerification = () => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { projectSeq, clarityAirVerificationSeq } = useParams();
  const [modifiedComponentData, setModifiedComponentData] = useState([]);
  const [requests, setRequests] = useState([]);
  const [systemMessages, setSystemMessages] = useState([]);
  const { creatorList = [] } = useContext(ScanToolSearchInfoContext) ?? {};
  const queryClient = useQueryClient();
  const { membersData: projectMembers } = useProjectMembers(projectSeq);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      localStorage.setItem("preferredTab", "CLARITYAIR");
      localStorage.setItem("lastProjectSeq", projectSeq);
    } catch (e) {
      console.error("Error setting localStorage items:", e);
    }

    return () => {
      try {
        localStorage.setItem("preferredTab", "CLARITYAIR");
        localStorage.setItem("lastProjectSeq", projectSeq);
      } catch (e) {
        console.error("Error setting localStorage items on unmount:", e);
      }
    };
  }, [projectSeq]);

  const completeAction = useMutationData(
    "/clarity_air_action_detail",
    {
      method: "POST",
      params: { actionType: "complete" },
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKeys.getClarityAirActionBySeq]);
      },
      onError: (error) => {
        console.error("Error completing action:", error);
      },
    },
  );

  const approveAction = useMutationData(
    "/clarity_air_action_detail",
    {
      method: "POST",
      params: { actionType: "approve" },
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKeys.getClarityAirActionBySeq]);
      },
      onError: (error) => {
        console.error("Error approving action:", error);
      },
    },
  );

  const rejectAction = useMutationData(
    "/clarity_air_action_detail",
    {
      method: "POST",
      params: { actionType: "reject" },
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKeys.getClarityAirActionBySeq]);
      },
      onError: (error) => {
        console.error("Error rejecting action:", error);
      },
    },
  );

  const createActionRequest = useMutationData(
    "/clarity_air_action_request",
    { method: "POST" },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKeys.getClarityAirActionBySeq]);
      },
      onError: (error) => {
        console.error("Error creating action request:", error);
      },
    },
  );

  const addVerificationComment = useMutationData(
    "/clarity_air_verification_verification_comment",
    { method: "PUT" },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKeys.getClarityAirActionBySeq]);
      },
      onError: (error) => {
        console.error("Error adding verification comment:", error);
      },
    },
  );

  const addFulfillmentComment = useMutationData(
    "/clarity_air_verification_fulfillment_comment",
    { method: "PUT" },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKeys.getClarityAirActionBySeq]);
      },
      onError: (error) => {
        console.error("Error adding fulfillment comment:", error);
      },
    },
  );

  const addUsageComment = useMutationData(
    "/clarity_air_verification_usage_comment",
    { method: "PUT" },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKeys.getClarityAirActionBySeq]);
      },
      onError: (error) => {
        console.error("Error adding usage comment:", error);
      },
      onMutate: (variables) => variables,
    },
  );

  const addUsageInputComment = useMutationData(
    "/clarity_air_usage_type_input_comment",
    { method: "PUT" },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKeys.getClarityAirActionBySeq]);
      },
      onError: (error) => {
        console.error("Error adding usage input comment:", error);
      },
      onMutate: (variables) => variables,
    },
  );

  const addFollowupCheckComment = useMutationData(
    "/clarity_air_check_follow_up_comment",
    { method: "PUT" },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKeys.getClarityAirActionBySeq]);
      },
      onError: (error) => {
        console.error("Error adding followup check comment:", error);
      },
      onMutate: (variables) => variables,
    },
  );

  const addAnalysisCheckComment = useMutationData(
    "/clarity_air_analysis_check_comment",
    { method: "PUT" },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKeys.getClarityAirActionBySeq]);
      },
      onError: (error) => {
        console.error("Error adding analysis check comment:", error);
      },
      onMutate: (variables) => variables,
    },
  );

  const [sourceCodeScanFollowupResults, setSourceCodeScanFollowupResults] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);

  const handleSave = () => {
    handleSaveSourceCodeScanComponents();
    setIsEditMode(false);
  };

  const {
    isLoading,
    isFetching,
    verificationStatus,
    approvalFlag,
    projectData,
    verificationData,
    projectId,
    verificationVersion,
    fetchedLicensePolicy,
    fetchedSourceCodeScanComponent,
    fetchedSourceCodeScanFollowupPlan,
    sourceCodeScanFollowupPlanUpdatedAt,
    fetchedSourceCodeAction,
    verificationReportsData,
    isReviewOrDoneStage,
    isNotAnalysisOrCheckAnalysisOrRequest,
    isFulfillmentOrCheckFulfillmentStage,
    fulfillmentInfoRef,
    handleUpdateStatus,
    handleMoveToReview,
    handleRejectVerification,
    handleMoveToDone,
    handleCompleteVerification,
    handleMoveToFulfillment,
    handleMoveToRequest,
    handleDecompleteFromDone,
    handleGetVerificationReportForPopup,
    handleToCompleteFulfillment,
    handleToRequestReVerification,
    handleMoveToUsageTypeCheck,
    handleMoveToUsageTypeInput,
    handleToFollowUpResult,
    handleRejectVerificationFromReview,
    handleRejectVerificationFromOnVerification,
    handleRejectUsageTypeCheck,
    handleRejectUseTypeInput,
    handleRejectUseTypeReview,
    handleSaveSourceCodeScanComponents,
  } = useClarityAirVerification(
    projectSeq,
    clarityAirVerificationSeq,
    modifiedComponentData,
    sourceCodeScanFollowupResults,
  );

  const currentVerificationData =
    verificationData && Object.keys(verificationData).length > 0
      ? {
          clarityAirVerificationSeq: verificationData.clarityAirVerificationSeq || clarityAirVerificationSeq,
          projectId: verificationData.projectId || projectData?.projectId,
          verificationVersion: verificationData.verificationVersion || verificationVersion,
          projectSeq,
          ...verificationData,
        }
      : null;

  const handleComponentDataChange = useCallback((data) => {
    setModifiedComponentData(data);
  }, []);

  useEffect(() => {
    if (fetchedSourceCodeAction?.requests) {
      const transformedRequests = fetchedSourceCodeAction.requests.map((request) => {
        const requesterInfo = creatorList.find((user) => user.value === request.createrId);
        const requesterRole = requesterInfo?.roleCdName || "unknown";
        const assigneeInfo = creatorList.find((user) => user.value === request.assigneeId);
        const assigneeRole = assigneeInfo?.roleCdName || "unknown";

        return {
          id: request.actionSeq,
          date: formatFullDateTime(request.createdAt),
          status: request.verificationStatusCdName || "Pending",
          actionStatus: request.actionStatus,
          requester: {
            requesterId: request.createrId,
            name: request.createrName || request.createrId || "Unknown",
            role: requesterRole,
          },
          assignee: {
            assigneeId: request.assigneeId,
            name: request.assigneeName || request.assigneeId || "Unknown",
            role: assigneeRole,
          },
          description: request.actionsContent || "No description provided",
          completionRequestDate: request.completionRequestDt || request.createdAt,
          cve: request.cve,
          conversation:
            request.clarityAirActionDetails?.map((detail) => {
              const userInfo = creatorList.find((user) => user.value === detail.createrId);
              const userRole = userInfo?.roleCdName || "Unknown";
              return {
                user: {
                  name: userInfo?.userName || userInfo?.label || "Unknown",
                  role: userRole,
                },
                status: detail.actionStatus,
                timestamp: formatFullDateTime(detail.createdAt),
                content: detail.actionContent || "",
              };
            }) || [],
        };
      });

      setRequests(transformedRequests);
    }

    const cveMessages = fetchedSourceCodeAction?.systemMessages || [];
    setSystemMessages(cveMessages);
  }, [fetchedSourceCodeAction, creatorList]);

  const getCurrentStepIndex = () => {
    const statusStepMap = {
      "Request ReVerification": 0,
      "Analyzing...": 1,
      "Analysis Check": 2,
      "Request Verification": 3,
      "Usage Type Check": 4,
      "Usage Type Input": 5,
      "Review Verification": 6,
      "Standby Followup": 7,
      "Fulfillment Completed": 8,
      Done: 9,
    };
    return statusStepMap[verificationData.verificationStatusCdName] || 0;
  };

  const shouldShowForceCompleteText = () => {
    const reqs = fetchedSourceCodeAction?.requests || [];
    if (reqs.length === 0) return false;
    return !reqs.every((request) => (request.actionStatus || "").toUpperCase() === "DONE");
  };

  const workflowSteps = [
    { label: "Request ReVerification", icon: null, displayLabel: t("fossid-status-options.reverification") },
    { label: "Analyzing", icon: AnalysisIcon, displayLabel: t("clarity-status-options.analyzing") },
    { label: "Analysis Check", icon: CheckAnalysisIcon, displayLabel: t("fossid-status-options.analysis-check") },
    { label: "Load Identifications", icon: LoadIdentifyIcon, displayLabel: t("common.load-identifications") },
    { label: "Usage type check", icon: UsageTypeCheckIcon, displayLabel: t("status.usageTypeCheck") },
    { label: "Usage type input", icon: UsageTypeInputIcon, displayLabel: t("status.usageTypeInput") },
    { label: "Review", icon: ReviewIcon, displayLabel: t("status.onReview") },
    { label: "Follow-up", icon: FollowUpIcon, displayLabel: t("status.standbyFollowup") },
    { label: "Check follow-up", icon: CheckFollowUpIcon, displayLabel: t("status.followupCheck") },
    { label: "Ready", icon: ReadyIcon, displayLabel: t("status.done") },
  ];

  const getRequestData = (requestId, actionContent) => {
    const request = fetchedSourceCodeAction?.requests?.find((req) => req.actionSeq === requestId);
    if (!request) {
      console.error("Request not found:", requestId);
      return null;
    }
    return {
      actionSeq: request.actionSeq,
      clarityAirVerificationSeq,
      actionContent,
    };
  };

  const handleComplete = (completeData) => {
    const { requestId, message } = completeData;
    const requestData = getRequestData(requestId, message);
    if (requestData) {
      completeAction.mutate(requestData);
    }
  };

  const handleApprove = (approvalData) => {
    const { requestId, message } = approvalData;
    const requestData = getRequestData(requestId, message);
    if (requestData) {
      approveAction.mutate(requestData);
    }
  };

  const handleReject = (rejectionData) => {
    const { requestId, message } = rejectionData;
    const requestData = getRequestData(requestId, message);
    if (requestData) {
      rejectAction.mutate(requestData);
    }
  };

  const handleAddRequest = (requestData) => {
    const completionRequestDt = new Date(requestData.completionRequestDate).toISOString();
    const requestPayload = {
      assigneeId: requestData.assignee,
      completionRequestDt,
      actionsContent: requestData.requestMessage,
      clarityAirVerificationSeq,
      ...(requestData.cve && { cve: requestData.cve }),
      ...(requestData.actionSeq && { actionSeq: requestData.actionSeq }),
    };
    createActionRequest.mutate(requestPayload);
  };

  const handleAddComment = (commentData) => {
    if (verificationData?.verificationStatusCd === "STANDBY_FOLLOWUP") {
      addFulfillmentComment.mutate(commentData);
    } else if (verificationData?.verificationStatusCd === "ON_REVIEW") {
      addVerificationComment.mutate(commentData);
    } else if (
      verificationData?.verificationStatusCd === "ON_VERIFICATION" &&
      verificationData?.verificationStatusCdName === "Usage Type Check"
    ) {
      addUsageComment.mutate(commentData);
    } else if (
      verificationData?.verificationStatusCd === "ON_VERIFICATION" &&
      verificationData?.verificationStatusCdName === "Usage Type Input"
    ) {
      addUsageInputComment.mutate(commentData);
    } else if (verificationData?.verificationStatusCd === "FOLLOWUP_CHECK") {
      addFollowupCheckComment.mutate(commentData);
    } else if (verificationData?.verificationStatusCd === "ANALYSIS_CHECK") {
      addAnalysisCheckComment.mutate(commentData);
    } else {
      console.error("Invalid verification status:", verificationData?.verificationStatusCd);
    }
  };

  return (
    <div className="min-h-full" data-testid="clarity-air-verification-page">
      <div className="p-4" data-testid="clarity-air-verification-header">
        <div className="space-y-3">
          <Breadcrumb
            data-testid="clarity-air-verification-breadcrumb"
            onBackClick={() => {
              navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
            }}
            items={[
              { label: t("navbar.project-list"), href: "/projects" },
              {
                label: t("navbar.project-details"),
                onClick: () => {
                  navigate(`/projects/${projectSeq}`, { state: { activeTab: "CLARITYAIR" } });
                },
              },
              { label: t("navbar.clarity-air-verification", "ClarityAIR Verification") },
            ]}
          />
          <div>
            <h1
              className={`text-2xl font-bold ${colors.textPrimary}`}
              data-testid="clarity-air-verification-page-title"
            >
              {verificationData.realFileName}
            </h1>
          </div>
        </div>
      </div>

      <div>
        <BasicInfoSection
          data-testid="clarity-air-verification-basic-info"
          data={{
            ...verificationData,
            projectName: projectData?.projectName || verificationData?.projectName,
          }}
          colors={colors}
          t={t}
          type={"clarityair"}
        />

        <div className={`px-6 py-6 rounded-2xl bg-[${colors.bgPrimaryColor}]`} data-testid="clarity-air-verification-workflow">
          <WorkflowProgressFossID
            verificationType="clarityair"
            steps={workflowSteps}
            approvalFlag={approvalFlag}
            currentStep={getCurrentStepIndex()}
            projectManagerId={projectData?.projectManagerId}
            onMoveToUsageTypeCheck={handleMoveToUsageTypeCheck}
            onMoveToUsageTypeInput={handleMoveToUsageTypeInput}
            onMoveToReview={handleMoveToReview}
            onMoveToLoadIdentifications={handleMoveToRequest}
            onRequestReVerification={handleToRequestReVerification}
            onMoveToFollowUp={handleMoveToFulfillment}
            onMoveToFollowCheckUp={handleToCompleteFulfillment}
            onMoveToDone={handleMoveToDone}
            onCompleteVerification={handleCompleteVerification}
            onDecompleteFromDone={handleDecompleteFromDone}
            onUpdateStatus={handleUpdateStatus}
            onRejectVerification={handleRejectVerification}
            onRejectUsageTypeCheck={handleRejectUsageTypeCheck}
            onRejectUsageTypeInput={handleRejectUseTypeInput}
            onRejectUsageTypeReview={handleRejectUseTypeReview}
            onToFollowUpResult={handleToFollowUpResult}
            onRejectVerificationFromReview={handleRejectVerificationFromReview}
            onRejectVerificationFromOnVerification={handleRejectVerificationFromOnVerification}
            currentVerificationData={currentVerificationData}
            forceCompleteText={shouldShowForceCompleteText()}
            isLoading={isLoading}
            isFetching={isFetching}
          />
        </div>
      </div>

      <div className="space-y-6 mt-6">
        <div className={`mt-5 p-6 ${colors.bgPrimary} rounded-2xl`} data-testid="clarity-air-verification-communication">
          <Communication
            requests={requests}
            systemMessages={systemMessages}
            verificationData={verificationData}
            verificationType="clarityair"
            verificationSeq={clarityAirVerificationSeq}
            onComplete={handleComplete}
            onApprove={handleApprove}
            onReject={handleReject}
            onAddRequest={handleAddRequest}
            onAddComment={handleAddComment}
            isAddCommentPending={
              addVerificationComment.isPending || addFulfillmentComment.isPending || addUsageComment.isPending
            }
            projectMembers={projectMembers}
            projectSeq={projectSeq}
            projectData={projectData}
            creatorList={creatorList}
          />
        </div>

        <div data-testid="clarity-air-verification-component-table">
        <ComponentTable
          data={fetchedSourceCodeScanComponent}
          licensePolicyData={fetchedLicensePolicy}
          onDataChange={handleComponentDataChange}
          isEditingAllowed={verificationStatus && verificationData.verificationStatusCdName === "Usage Type Input"}
          seq="sourceCodeScanComponentSeq"
          onSave={handleSave}
          className="p-4"
          allowVexReview
          showShieldFilter
          vexContext={{
            scanToolCd: "clarityair",
            verificationSeq: clarityAirVerificationSeq,
            projectSeq,
            projectManagerId: projectData?.projectManagerId,
            verificationStatusCd: verificationData?.verificationStatusCd,
          }}
        >
          <ComponentTableHeader
            title={t("common.component-list")}
            count={fetchedSourceCodeScanComponent?.length || 0}
            colors={colors}
            t={t}
          />
        </ComponentTable>
        </div>

        {(isReviewOrDoneStage || isFulfillmentOrCheckFulfillmentStage) && (
          <FulfillmentInfo
            key={sourceCodeScanFollowupPlanUpdatedAt}
            data-testid="clarity-air-verification-fulfillment-info"
            ref={fulfillmentInfoRef}
            fulfillmentData={fetchedSourceCodeScanFollowupPlan}
            verificationStatus={verificationStatus}
            projectManagerId={projectData?.projectManagerId}
          />
        )}

        {isNotAnalysisOrCheckAnalysisOrRequest && (
          <VerificationReportInfo
            data-testid="clarity-air-verification-report-info"
            projectId={projectId}
            projectManagerId={projectData?.projectManagerId}
            verificationVersion={verificationVersion}
            handleGetVerificationReportForPopup={handleGetVerificationReportForPopup}
            verificationReportsData={verificationReportsData}
            verificationToolCd="CLARITYAIR"
            verificationSeq={clarityAirVerificationSeq}
          />
        )}
      </div>
    </div>
  );
};

export default ClarityAIRVerification;
